import { type ClassValue } from "clsx"
import { type FC } from "react"
import { Link } from "react-router"

import { type FCC } from "@forento/shared-client/types/react"
import { cn } from "@forento/shared-client/utilities/tailwind"

import { useUser } from "~/contexts/UserContext"
import Button from "~/themes/school/components/Button"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import { lightTextColor } from "~/themes/school/styles"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const ActiveCourses: FC<{ className?: ClassValue }> = ({ className }) => {
	const user = useUser().user
	const t = useTranslation()

	const { data: courses, error } = query.course.listActive.useQuery(undefined, { enabled: user !== null })

	if (user === null) {
		return (
			<CenteredFrame className={className}>
				<p>{t("dashboard.signInToViewActiveCourses")}</p>
				<Button variant="primary" onClick={routes.user.signin()}>
					{t("user.signIn.title")}
				</Button>
			</CenteredFrame>
		)
	}

	if (error) {
		return (
			<CenteredFrame className={className}>
				<p>{t("failedToLoad")}</p>
			</CenteredFrame>
		)
	}

	if (courses === undefined) {
		return (
			<CenteredFrame className={className}>
				<PartialLoadingPage />
			</CenteredFrame>
		)
	}

	if (courses.length === 0) {
		return (
			<CenteredFrame className={className}>
				<p>{t("dashboard.noActiveCourses")}</p>
				<Button variant="primary" onClick={routes.course.index()}>
					{t("course.goToAll")}
				</Button>
			</CenteredFrame>
		)
	}

	return (
		<Frame className={className}>
			{[...courses]
				.sort((a, b) => (a.progress === 1 ? 1 : b.progress === 1 ? -1 : b.progress - a.progress))
				.map(course => (
					<Link
						key={course.id}
						className="grid grid-cols-2 grid-rows-2 gap-4 px-4 py-6 not-last:border-b not-last:border-b-background"
						to={routes.course.detail(course.id)}
					>
						<div className="font-bold">{course.title}</div>
						<p className="text-right text-light">{Math.floor(course.progress * 100)}%</p>
						<div className="col-span-2 h-1 bg-background">
							<div
								className="h-full"
								style={{
									width: `${course.progress * 100}%`,
									backgroundColor: getColor(course.progress),
								}}
							/>
						</div>
					</Link>
				))}
		</Frame>
	)
}

const Frame: FCC<{ className?: ClassValue }> = ({ className, children }) => {
	const t = useTranslation()

	return (
		<div className={cn("flex flex-col bg-white p-6 shadow", className)}>
			<h2 className="font-quicksand font-bold">{t("dashboard.activeCourses")}</h2>
			{children}
		</div>
	)
}

const CenteredFrame: FCC<{ className?: ClassValue }> = ({ className, children }) => (
	<Frame className={className}>
		<div className="flex grow flex-col items-center justify-center gap-4">{children}</div>
	</Frame>
)

function getColor(progress: number) {
	if (progress < 0.2) return lightTextColor
	if (progress < 0.4) return "#FF6B6B"
	if (progress < 0.6) return "#DAD873"
	if (progress < 0.8) return "#4ECDC4"
	if (progress < 1) return "#45B7D1"
	return "#1BC961"
}

export default ActiveCourses
